import { useAsyncData, useNuxtApp, useRoute } from '#imports'
import { computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import { getOptionalEndpointTimeoutOptions } from '~/scopes/product-core/composables/useFetch/utils/getOptionalEndpointTimeoutOptions'
import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

/**
 * Server-side fetch of the mobile-plan pickers (PPI-7770), successor to
 * `useFetchServerSideServicePickers` (PPI-7740) now that the backend split
 * `services/pickers` into `services/mobile-plan-pickers` and `services/trade-in-pickers`.
 *
 * Unlike the old endpoint, `mobile-plan-pickers` is session-free and cacheable, so this
 * runs unconditionally (`server: true`, no country/URL gating) for every PDP load. It is
 * the single source of mobile-plan picker data for the whole page:
 * - it drives the SEO JSON-LD subsidised price (awaited during SSR, before the HTML —
 *   including the JSON-LD script — is streamed, so there is no price flicker),
 * - it's merged with the client-only `useFetchTradeInPickers` inside `useFetchServicePickers`
 *   to feed the UI.
 *
 * It is "optional": on failure it leaves `data` empty and the JSON-LD falls back
 * to the non-subsidised price (graceful degradation).
 */
export function useFetchMobilePlanPickers() {
  const nuxtApp = useNuxtApp()
  const route = useRoute()

  const { productId, mobilePlan } = useUrlParams(route)
  const $httpFetch = useHttpFetch()
  const experiments = useExperiments()

  const withFreenetMobilePlans =
    experiments['experiment.ppFreenetMobilePlans'] === 'withFreenetMobilePlans'

  const mobilePlanPickersQueryParams = computed(() => {
    return removeEmptyValuesInObject({
      mobile_plan_offer_id: mobilePlan.value ? String(mobilePlan.value) : null,
      with_freenet_mobile_plans: withFreenetMobilePlans,
    })
  })

  return useAsyncData(
    () =>
      getAsyncDataKey('pp-mobile-plan-pickers', {
        productId,
        mobilePlan: mobilePlan.value,
        withFreenetMobilePlans,
      }),
    async () =>
      $httpFetch(
        '/product-page/products/:productId/services/mobile-plan-pickers',
        {
          pathParams: { productId },
          query: mobilePlanPickersQueryParams.value,
          ...getOptionalEndpointTimeoutOptions(),
        },
      ),
    {
      dedupe: 'defer',
      getCachedData: (key) => nuxtApp.payload.data[key],
    },
  )
}
